<template>
  <div>
    <DataTable
      ref="DataTable"
      :data-model="'products'"
      :items="products"
      :loading="loading"
      @reloadData="reload()"
      @input="onDataTableInput"
      :display-name-plural="$t('entities.PendingProducts.Plural')"
      :display-name-singular="$t('entities.Product.Singular')"
      :permission-name="'Products'"
      :allow-delete="false"
      :items-per-page="itemsPerPage"
      :sort-compare="(aRow, bRow, key) => {
        if(key === 'doctor') {
          const a = aRow[key]
          const b = bRow[key]
          if(a === email && b === email) {
            return 0;
          } else if(a === email && b !== email) {
            return 1;
          } else if(a !== email && b === email) {
            return -1;
          }
          return 0;
        } else {
          return null;
        }
      }"
      :sort-by="{doctor: true}"
      :allow-create="false"
      :exclude-fields="['id', 'publishedAt']">
      <template #cell(status)="row">
        {{ $t('products.Status.'+row.item.status) }}
      </template>
      <template #cell(color)="row">
        <div :style="'min-height:15px;background-color:'+(colors.filter(x => x.id == row.item.color)[0].value)"
             v-if="row.item.color !== 0" />
      </template>
      <template #cell(approvedByLaboratory)="row">
        <b-form-checkbox v-model="row.item.approvedByLaboratory"
                         @change="productToApproveItem = row.item;"
                         :disabled="!isApproveByLaboratoryAuthorized" />
      </template>
      <template #extraActions="row">
        <el-tooltip
          :content="$t('common.DownloadPDF')"
          :enterable="false"
          placement="top">
          <b-button class="downloadBtn mx-1"
                    @click="download(row.item)">
            <font-awesome-icon class="fa-xs"
                               icon="download" />
          </b-button>
        </el-tooltip>
        <el-tooltip
          :content="$t('common.DownloadDoctorsIntroPDF')"
          :enterable="false"
          placement="top">
          <b-button class="downloadBtnDoctor mx-1"
                    @click="downloadDoctorsIntro(row.item)">
            <font-awesome-icon class="fa-xs"
                               icon="file-download" />
          </b-button>
        </el-tooltip>
        <el-tooltip
          :content="$t('common.AssignDoctorToProduct')"
          :enterable="false"
          placement="top"
          v-if="isMainDoctor">
          <b-button class="assignBtnDoctor mx-1"
                    @click="productToAssign = row.item.id; $refs.assignModal.show();">
            <font-awesome-icon class="fa-md"
                               icon="briefcase-medical" />
          </b-button>
        </el-tooltip>
      </template>
    </DataTable>
    <b-modal id="modal-center"
             ref="downloadModal"
             centered
             :ok-disabled="true"
             :static="true">
      <template #modal-header>
        <div class="d-flex justify-content-center w-100 text-center">
          <h3>
            {{ $t('common.Downloading') }}
          </h3>
        </div>
      </template>
      <p class="my-4 text-center">
        {{ $t('common.GeneratingPDF') }}
      </p>
      <div id="nprogressDownload"
           style="height:25px" />
      <template #modal-footer="{ cancel, ok }">
        <b-button
          variant="danger"
          @click="cancelDownload(); $refs.downloadModal.hide()">
          {{ $t('common.Cancel') }}
        </b-button>
      </template>
    </b-modal>
    <b-modal id="modal-center"
             ref="assignModal"
             centered
             :ok-disabled="true"
             :static="true"
             v-if="isMainDoctor">
      <template #modal-header>
        <div class="d-flex justify-content-center w-100 text-center">
          <h3>
            {{ $t('common.AssignDoctorToProduct') }}
          </h3>
        </div>
      </template>
      <DoctorInput v-model="doctorToAssign" />
      <template #modal-footer="{ cancel, ok }">
        <b-button
          variant="danger"
          @click="doctorToAssign = 0; productToAssign = 0; $refs.assignModal.hide()">
          {{ $t('common.Cancel') }}
        </b-button>
        <b-button
          variant="success"
          @click="assignDoctorToProduct(); $refs.assignModal.hide()">
          {{ $t('common.Edit') }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>
<script>
import DataTable from '@components/admin/DataTable';
import NProgress from 'nprogress/nprogress';
import DoctorColors from '@utils/doctorColors';
import { downloadBlob } from '@utils/downloadBlob';
import DoctorInput from '@components/input/DoctorInput';
import { updatePairedPhenotypesCountMixin } from '@components/mixins/updatePairedPhenotypesCount';

export default {
  components: {
    DataTable,
    DoctorInput
  },
  mixins: [
    updatePairedPhenotypesCountMixin
  ],
  props: {
    itemsPerPage: {
      type: Number,
      required: false,
      default: 10
    }
  },
  data() {
    return {
      source: null,
      colors: DoctorColors,
      doctorToAssign: 0,
      productToAssign: 0,
      productToApproveItem: null,
      products: [],
      loading: true,
      isMainDoctor: false
    };
  },
  computed: {
    email() {
      return this.$store.state['vue-auth'].user.email;
    },
    approvedByLaboratory() {
      return this.productToApproveItem?.approvedByLaboratory;
    },
    isApproveByLaboratoryAuthorized() {
      return this.isAdmin || this.isLaborant;
    },
    role() {
      return this.$store.state['vue-auth']?.user?.role || '';
    },
    isLaborant() {
      return this.role === 'Laboratory Scientist';
    },
    isAdmin() {
      return this.role === 'Administrator';
    }
  },
  created() {
    this.reload();
    this.axios.get('/admin/auth/ismaindoctor')
      .then((res) => {
        this.isMainDoctor = res.data.result;
      });
  },
  mounted() {
    this.source = this.axios.CancelToken.source();
  },
  watch: {
    approvedByLaboratory(newValue, oldValue) {
      if (oldValue !== undefined) {
        this.approveProduct();
      }
    }
  },
  methods: {
    cancelDownload() {
      this.source.cancel();
      this.source = this.axios.CancelToken.source();
    },
    reload() {
      this.loading = true;
      this.axios.get('/admin/products/getallpending').then((res) => {
        if (res.status === 200 && res.data.result.length > 0) {
          this.products = res.data.result;
        } else {
          this.products = [];
        }
        this.loading = false;
      }).catch((err) => Promise.reject(err)).finally(() => {
        this.$refs.DataTable.finishSpinner();
      });
    },
    download(item) {
      this.$refs.downloadModal.show();
      NProgress.configure({ parent: '#nprogressDownload' });
      NProgress.start();
      this.axios.get('admin/products/getpdf', {
        params: {
          ProductId: item.id
        },
        responseType: 'arraybuffer',
        cancelToken: this.source.token
      }).then((response) => {
        const today = new Date();
        downloadBlob(
          response.data,
          `GenScan_Vysledek_${today.toLocaleDateString('en-US')} ${item.customer}.pdf`,
          'application/pdf'
        );
        this.$refs.downloadModal.hide();
      })
        .catch((thrown) => {
          if (this.axios.isCancel(thrown)) {
            this.$root.$bvToast.toast(this.$t('common.DownloadCancel'), {
              title: this.$t('common.Cancelled'),
              variant: 'danger',
              toaster: 'b-toaster-top-right'
            });
            this.$refs.downloadModal.hide();
          }
        })
        .finally(() => {
          NProgress.configure({ parent: 'body' });
          NProgress.done();
        });
    },
    downloadDoctorsIntro(item) {
      this.$refs.downloadModal.show();
      NProgress.configure({ parent: '#nprogressDownload' });
      NProgress.start();
      this.axios.get('admin/products/getdoctorsintropdf', {
        params: {
          ProductId: item.id
        },
        responseType: 'arraybuffer',
        cancelToken: this.source.token
      }).then((response) => {
        const today = new Date();
        downloadBlob(
          response.data,
          `GenScan_Lekarska_Zprava_${today.toLocaleDateString('en-US')} ${item.customer}.pdf`,
          'application/pdf'
        );
        this.$refs.downloadModal.hide();
      })
        .catch((thrown) => {
          if (this.axios.isCancel(thrown)) {
            this.$root.$bvToast.toast(this.$t('common.DownloadCancel'), {
              title: this.$t('common.Cancelled'),
              variant: 'danger',
              toaster: 'b-toaster-top-right'
            });
            this.$refs.downloadModal.hide();
          }
        })
        .finally(() => {
          NProgress.configure({ parent: 'body' });
          NProgress.done();
        });
    },
    assignDoctorToProduct() {
      this.axios.patch('admin/products/assigndoctor', {
        DoctorId: this.doctorToAssign,
        ProductId: this.productToAssign
      }).then(() => {
        this.$root.$bvToast.toast(this.$t('ViewReservations.SuccessToast.Body'), {
          title: this.$t('ViewReservations.SuccessToast.Title'),
          variant: 'success',
          toaster: 'b-toaster-top-right'
        });
        this.doctorToAssign = 0;
        this.productToAssign = 0;
        this.$refs.DataTable.refresh();
      }).catch(() => {
        this.$root.$bvToast.toast(this.$t('ViewReservations.FailToast.Body'), {
          title: this.$t('ViewReservations.FailToast.Title'),
          variant: 'danger',
          toaster: 'b-toaster-top-right'
        });
      });
    },
    approveProduct() {
      this.axios.patch('admin/products/approveByLaboratory', {
        ProductId: this.productToApproveItem.id,
        Approve: this.productToApproveItem.approvedByLaboratory
      }).then(() => {
        this.$root.$bvToast.toast(this.$t('ViewReservations.SuccessToast.Body'), {
          title: this.$t('ViewReservations.SuccessToast.Title'),
          variant: 'success',
          toaster: 'b-toaster-top-right'
        });
        this.productToApprove = null;
        this.$refs.DataTable.refresh();
      }).catch(() => {
        this.$root.$bvToast.toast(this.$t('ViewReservations.FailToast.Body'), {
          title: this.$t('ViewReservations.FailToast.Title'),
          variant: 'danger',
          toaster: 'b-toaster-top-right'
        });
      });
    }
  }
};
</script>
<style lang="scss" scoped>
@import '@colors';
.downloadBtn {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  background-color: #c95328;
  color: $text-on-secondary;
  border: none;
  &:hover {
    box-shadow: 0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22);
    background-color: darken($secondary-variant-color, 10%);
    color: $text-on-secondary;
  }
}

.downloadBtnDoctor {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  background-color: #3e67ec;
  color: $text-on-secondary;
  border: none;
  &:hover {
    box-shadow: 0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22);
    background-color: darken($secondary-variant-color, 10%);
    color: $text-on-secondary;
  }
}

.assignBtnDoctor {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  background-color: #3ee6ec;
  color: $text-on-secondary;
  border: none;
  &:hover {
    box-shadow: 0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22);
    background-color: darken($secondary-variant-color, 10%);
    color: $text-on-secondary;
  }
}
</style>
